// The call's footprint OUTSIDE the huddle:
//  - `CallRowSlot`: the sidebar row's live-call marker (HuddleRowMarker: headphones + up to three
//    faces; "a live call outranks the clock", clicking it joins);
//  - `CallHeaderButton`: the conversation header's phone (MacConversationView `Strings.Call.start`);
//  - `CallConversationStrips`: under the header — the call's notice, the "call in progress · Join"
//    strip (hidden while this device is ON that call), and the slim call bar (MacCallBar) that the
//    Mac draws only when the sidebar — and with it the huddle panel — is NOT on screen (here: phone
//    width, where the open conversation hides the list);
//  - `IncomingCallCard`: the ring (MacIncomingCallSheet), hosted at the workspace root;
//  - `CallHost`: mounts the window and the card, wires the manager, and ⌘⇧H / Ctrl+Shift+H.
import { useEffect, useState, type ReactNode } from 'react';
import { ActiveCalls, type ActiveCall } from '../../../core/activeCalls';
import { t, tf } from '../../../i18n/i18n';
import { Icon } from '../../../design/icons';
import { Avatar } from '../../Avatar';
import { callManager, NOTICE_KEYS } from './callManager';
import { CallIcon } from './callIcons';
import { HuddleWindow } from './Huddle';
import { useActiveCall, useActiveCalls, useCallState } from './useCalls';
import './calls.css';

function participantText(call: ActiveCall): string {
  const n = ActiveCalls.participantCount(call);
  return n === 0 ? t('call.starting') : n === 1 ? t('call.participants.one') : tf('call.participants.many', n);
}

export function HuddleRowMarker({ call, onJoin, ring }: { call: ActiveCall; onJoin(): void; ring?: 'raised' | 'sunken' }) {
  const view = useActiveCalls();
  const ids = ActiveCalls.uniqueUserIds(call).slice(0, 3);
  return (
    <span
      role="button"
      tabIndex={0}
      className={`huddle-row-marker${ring === 'raised' ? ' on-raised' : ''}`}
      title={tf('call.sidebar.active', ActiveCalls.participantCount(call))}
      aria-label={tf('call.sidebar.active', ActiveCalls.participantCount(call))}
      data-testid="huddle-row-marker"
      // The row selects on mousedown: the marker's press must stay the marker's.
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        e.stopPropagation();
        onJoin();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          onJoin();
        }
      }}
    >
      <CallIcon name="headphones" size={11} strokeWidth={2.4} />
      <span className="huddle-row-faces">
        {ids.map((id) => (
          <Avatar key={id} userId={id} name={view.people[id]?.name ?? '?'} size={14} version={view.people[id]?.version ?? 0} />
        ))}
      </span>
    </span>
  );
}

/** In a sidebar row: the marker when that conversation has a live call, else the row's own trailing. */
export function CallRowSlot({ channelId, channelName, children }: { channelId: string; channelName: string; children: ReactNode }) {
  const call = useActiveCall(channelId);
  const state = useCallState();
  if (!call || call.sessions.length === 0) return <>{children}</>;
  const inThisCall = state.phase !== 'idle' && state.channelId === channelId;
  return <HuddleRowMarker call={call} onJoin={() => !inThisCall && callManager.join(channelId, channelName)} />;
}

export function CallHeaderButton({ channelId, channelName }: { channelId: string; channelName: string }) {
  const view = useActiveCalls();
  if (view.unavailable) return null;
  return (
    <button className="icon-button" onClick={() => callManager.join(channelId, channelName)} title={t('call.start')} aria-label={t('call.start')} data-testid="call-start">
      <Icon name="phone" size={17} />
    </button>
  );
}

let mountedStrips = 0;
const stripListeners = new Set<() => void>();

export function CallConversationStrips({ channelId, channelName }: { channelId: string; channelName: string }) {
  const state = useCallState();
  const call = useActiveCall(channelId);
  useEffect(() => {
    mountedStrips++;
    stripListeners.forEach((l) => l());
    return () => {
      mountedStrips--;
      stripListeners.forEach((l) => l());
    };
  }, []);
  const inThisCall = state.phase !== 'idle' && state.channelId === channelId;
  const showsCall = !!call && !inThisCall;
  if (state.phase === 'idle' && !state.notice && !showsCall) return null;
  return (
    <div className="call-strips">
      {state.phase !== 'idle' ? <PhoneCallBar /> : null}
      {state.notice ? <NoticeStrip /> : null}
      {call && showsCall ? (
        <div className="call-strip" role="status" data-testid="call-strip">
          <HuddleRowMarker call={call} onJoin={() => callManager.join(channelId, channelName)} ring="raised" />
          <span className="call-strip-text">
            {t('call.active.title')} · {participantText(call)}
          </span>
          <button type="button" className="call-join-button" onClick={() => callManager.join(channelId, channelName)}>
            {t('call.join')}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function NoticeStrip() {
  const state = useCallState();
  if (!state.notice) return null;
  return (
    <div className="call-notice-strip" role="alert">
      <span>{t(NOTICE_KEYS[state.notice])}</span>
      <button type="button" className="icon-button" onClick={() => callManager.consumeNotice()} aria-label={t('common.close')} title={t('common.close')}>
        <CallIcon name="x" size={13} strokeWidth={2.2} />
      </button>
    </div>
  );
}

/** MacCallBar — CSS shows it only at phone width, where the sidebar's huddle panel is off screen. */
function PhoneCallBar() {
  const state = useCallState();
  const count = state.participants.length;
  const title = state.phase === 'joining' ? t('call.connecting') : count === 1 ? t('call.participants.one') : tf('call.participants.many', count);
  return (
    <div className="phone-call-bar" data-testid="phone-call-bar">
      <span className={`phone-call-dot${state.phase === 'active' ? ' active' : ''}`} />
      <button type="button" className="phone-call-title" onClick={() => callManager.setExpanded(true)} title={t('call.expand')}>
        <span>{title}</span>
        {state.channelName ? <span className="muted">{state.channelName}</span> : null}
      </button>
      <button type="button" className="icon-button" onClick={() => callManager.setExpanded(true)} title={t('call.roster')} aria-label={t('call.roster')}>
        <CallIcon name="people" size={16} />
      </button>
      <button
        type="button"
        className={`icon-button${state.sharingScreen ? ' call-tint-warning' : ''}`}
        onClick={() => (state.sharingScreen ? callManager.stopScreenShare() : callManager.requestScreenShare())}
        title={state.sharingScreen ? t('call.control.stopShare') : t('call.control.share')}
        aria-label={state.sharingScreen ? t('call.control.stopShare') : t('call.control.share')}
      >
        <CallIcon name={state.sharingScreen ? 'screenStop' : 'screen'} size={16} />
      </button>
      <button type="button" className="icon-button" onClick={() => callManager.toggleMute()} title={state.muted ? t('call.unmute') : t('call.mute')} aria-label={state.muted ? t('call.unmute') : t('call.mute')}>
        <CallIcon name={state.muted ? 'micSlash' : 'mic'} size={16} />
      </button>
      <button type="button" className="icon-button call-tint-leave" onClick={() => callManager.leave()} title={t('call.leave')} aria-label={t('call.leave')}>
        <CallIcon name="phoneDown" size={17} filled strokeWidth={1.2} />
      </button>
    </div>
  );
}

export function IncomingCallCard() {
  const state = useCallState();
  const incoming = state.incoming;
  const view = useActiveCalls();
  if (!incoming) return null;
  const person = view.people[incoming.callerId];
  return (
    <div className="scrim incoming-call-scrim" onMouseDown={(e) => e.target === e.currentTarget && callManager.declineIncoming()}>
      <div className="incoming-call-card" role="alertdialog" aria-label={t('call.incoming.title')} data-testid="incoming-call">
        <span className="incoming-call-avatar">
          <Avatar userId={incoming.callerId} name={incoming.callerName || '?'} size={56} version={person?.version ?? 0} />
        </span>
        <h2>{t('call.incoming.title')}</h2>
        <p>{tf('call.incoming.from', incoming.callerName || t('channelList.unknownUser'))}</p>
        <div className="incoming-call-actions">
          <button type="button" className="incoming-decline" onClick={() => callManager.declineIncoming()}>
            <CallIcon name="phoneDown" size={16} filled strokeWidth={1.2} />
            {t('call.decline')}
          </button>
          <button type="button" className="incoming-accept" onClick={() => callManager.acceptIncoming()} autoFocus>
            <CallIcon name="headphones" size={16} strokeWidth={2} />
            {t('call.accept')}
          </button>
        </div>
      </div>
    </div>
  );
}

/** A notice raised while no conversation is on screen still has to be said somewhere. */
function FloatingNotice() {
  const [, setTick] = useState(0);
  useEffect(() => {
    const l = () => setTick((n) => n + 1);
    stripListeners.add(l);
    return () => void stripListeners.delete(l);
  }, []);
  const state = useCallState();
  if (!state.notice || mountedStrips > 0) return null;
  return (
    <div className="call-floating-notice">
      <NoticeStrip />
    </div>
  );
}

/**
 * Mounted once by the workspace. `channelId`/`channelName` are the conversation on screen — what
 * ⌘⇧H / Ctrl+Shift+H toggles (Slack's huddle key: start or join here, or leave if already here).
 */
export function CallHost({ me, serverBase, channelId, channelName }: { me: string | null; serverBase: string; channelId: string | null; channelName: string }) {
  useEffect(() => callManager.wire(me, serverBase), [me, serverBase]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || !e.shiftKey || e.altKey || e.key.toLowerCase() !== 'h' || !channelId) return;
      e.preventDefault();
      const state = callManager.getState();
      if (state.phase !== 'idle' && state.channelId === channelId) callManager.leave();
      else callManager.join(channelId, channelName);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [channelId, channelName]);
  return (
    <>
      <HuddleWindow />
      <IncomingCallCard />
      <FloatingNotice />
    </>
  );
}
